import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { trackAnalyticsEvent } from 'app/actions/AnalyticsGa4Actions';
import {
  eventNames,
  UI_COMPONENT_TYPE,
  METADATA_CATEGORY_TYPE,
  UI_CONTAINER_TYPE
} from 'app/constants/AnalyticsGa4Constants';
import {
  mapDisplayBibToAnalyticsBibEntity,
  mapSearchBibAvailabilityToDisplayAvailability,
  mapSearchBibToDisplayBib
} from 'app/helpers/displayBib';
import { hasStackMap } from 'app/helpers/stackMap/stackMapHelpers';
import { selectCatalogBib } from 'app/selectors/catalogBibs/CatalogBibSelector';
import { selectBibEntities } from 'app/selectors/EntitiesSelector';
import bibItemShape from 'app/shapes/bibItemShape';
import stackMapStateShape from 'app/shapes/stackMapStateShape';

import StackMapButton from './StackMapButton';

const StackMapButtonContainer = ({
  bibItem,
  fetchStackMap,
  itemPosition,
  itemPositionGrouped,
  stackMapState,
  totalCount
}) => {
  const dispatch = useDispatch();

  const intl = useIntl();
  const label = intl.formatMessage({ id: 'shelf_map' });

  const searchBibs = useSelector(selectBibEntities);
  const catalogBib = useSelector(selectCatalogBib);

  const searchBib = searchBibs.get(catalogBib?.get('metadataId'))?.toJS() ?? null;

  // TODO: The search page specific values should come as props.
  // The component shouldn't check window.location to figure out what page it is on.
  const currentView = useSelector(state => state.getIn(['search', 'catalogSearch', 'view']));
  const pagination = useSelector(state => state.getIn(['search', 'catalogSearch', 'pagination']));
  const sortBys = useSelector(state => state.getIn(['search', 'catalogSearch', 'sortBys']));

  const { settings } = stackMapState;
  const hasStackMapForBibItem = hasStackMap(settings, bibItem.toJS());
  if (!hasStackMapForBibItem) {
    return null;
  }

  const onClick = () => {
    const domain = settings.domain;
    const location = bibItem.get('collection');
    const callno = bibItem.get('callNumber');
    const library = bibItem.getIn(['branch', 'name']);

    fetchStackMap(domain, {
      location,
      callno,
      library
    });

    const isFromSearchPage = window?.location?.pathname === '/v2/search';

    // Note: `catalogBib` will be in Redux state only when this component is rendered on the Bib Page.
    const bibEntity = mapDisplayBibToAnalyticsBibEntity(
      mapSearchBibToDisplayBib(searchBib),
      mapSearchBibAvailabilityToDisplayAvailability(searchBib?.availability)
    );

    dispatch(
      trackAnalyticsEvent({
        entities: {
          bib: bibEntity,
          location: {
            location_id: bibItem.getIn(['branch', 'code']),
            location_name: bibItem.getIn(['branch', 'name'])
          },
          metadata: {
            metadata_category_type: METADATA_CATEGORY_TYPE.HOLDINGS_RESOURCE_ITEM_ID,
            metadata_value: bibItem.get('itemId')
          },
          ui: {
            ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
            ui_component_label: label,
            ...(isFromSearchPage && {
              ui_content_position: itemPosition,
              ui_content_position_grouped: itemPositionGrouped,
              ui_content_position_grouped_count: totalCount,
              ui_container_type: UI_CONTAINER_TYPE.ITEM,
              ui_content_layout: currentView,
              ui_content_page: pagination.get('page'),
              ui_content_sort: sortBys.find(sortBy => sortBy.get('applied'))?.get('id'),
              ui_content_total_count: pagination.get('count')
            })
          }
        },
        eventName: eventNames.HOLDINGS_WAYFINDER_VIEW
      })
    );
  };

  return <StackMapButton label={label} onClick={onClick} />;
};

StackMapButtonContainer.propTypes = {
  bibItem: bibItemShape.isRequired,
  fetchStackMap: PropTypes.func.isRequired,
  itemPosition: PropTypes.number,
  itemPositionGrouped: PropTypes.number,
  stackMapState: stackMapStateShape.isRequired,
  totalCount: PropTypes.number
};

export default StackMapButtonContainer;
